import { computed, ref, watch } from 'vue'
import type { FormKitFrameworkContext } from '@formkit/core'

export type AutoCompleteOption = { id: string; label: string }

interface FormKitAutoCompleteProps {
  context?: FormKitFrameworkContext & {
    placeholder?: string
    disabled?: boolean
  }
  modelValue?: string | null
  placeholder?: string
  disabled?: boolean
}

interface UseFormKitAutoCompleteOptions {
  props: FormKitAutoCompleteProps
  emit: (event: 'update:modelValue', value: string) => void
  search: (query: string) => Promise<AutoCompleteOption[]>
  loadById: (id: string) => Promise<AutoCompleteOption | null>
  maxCacheSize?: number
}

export function useFormKitAutoComplete({
  props,
  emit,
  search,
  loadById,
  maxCacheSize = 100,
}: UseFormKitAutoCompleteOptions) {
  const cache = ref<Map<string, AutoCompleteOption>>(new Map())
  const suggestions = ref<AutoCompleteOption[]>([])
  const resolvedOption = ref<AutoCompleteOption | null>(null)

  const isFormKitMode = computed(() => props.context?.node != null)
  // FormKit keeps the explicit `id` prop in attrs and uses context.id for its
  // generated id. Prefer the explicit id so external labels target the real input.
  const inputId = computed(() => {
    const id = props.context?.attrs?.id
    return id != null && String(id).trim() !== ''
      ? String(id)
      : (props.context?.id ?? undefined)
  })

  const isRequired = computed(() => {
    if (!props.context?.node?.props?.validation) return false
    const v = props.context.node.props.validation
    return (
      typeof v === 'string' && v.split('|').some((r) => r.trim() === 'required')
    )
  })

  const disabled = computed(() =>
    Boolean(props.context?.disabled ?? props.disabled)
  )

  // The id is passed to PrimeVue's inner input via inputId; leaving it on the
  // wrapper would create duplicate ids and break label association.
  const attrs = computed(() => {
    const { id: _id, ...rest } = props.context?.attrs ?? {}
    return rest
  })

  const rawValue = computed(() =>
    isFormKitMode.value
      ? (props.context?._value as string | undefined)
      : props.modelValue
  )

  const valueId = computed(() => {
    const v = rawValue.value
    if (v == null || v === '') return ''
    return typeof v === 'string' ? v : (v as AutoCompleteOption).id
  })

  const displayPlaceholder = computed(() => {
    const p = isFormKitMode.value
      ? props.context?.placeholder
      : props.placeholder
    return p != null && String(p).trim() !== '' ? String(p).trim() : '\u00A0'
  })

  const displayValue = computed(() => {
    if (resolvedOption.value && resolvedOption.value.id === valueId.value) {
      return resolvedOption.value
    }
    if (valueId.value && cache.value.has(valueId.value)) {
      return cache.value.get(valueId.value)!
    }
    return valueId.value ? { id: valueId.value, label: valueId.value } : null
  })

  function setCachedOption(opt: AutoCompleteOption): void {
    const c = cache.value
    if (c.size >= maxCacheSize) {
      const oldestKey = c.keys().next().value
      if (oldestKey != null) c.delete(oldestKey)
    }
    c.set(opt.id, opt)
  }

  async function onSearch(event: { query: string }) {
    const results = await search(event.query)
    results.forEach(setCachedOption)
    suggestions.value = results
  }

  function onSelect(value: AutoCompleteOption | string | null) {
    if (typeof value === 'string') {
      return
    }

    const id = value == null ? '' : value.id
    if (isFormKitMode.value && props.context?.node) {
      props.context.node.input(id)
    } else if (!isFormKitMode.value) {
      emit('update:modelValue', id)
    }
  }

  async function resolveCurrentId() {
    const id = valueId.value
    if (!id) {
      resolvedOption.value = null
      return
    }
    if (cache.value.has(id)) {
      resolvedOption.value = cache.value.get(id)!
      return
    }
    const opt = await loadById(id)
    if (opt) setCachedOption(opt)
    resolvedOption.value = opt
  }

  watch(valueId, resolveCurrentId, { immediate: true })

  const wrapperClass = computed(
    () => 'w-full formkit-primevue formkit-primevue--clearable'
  )

  return {
    suggestions,
    displayValue,
    displayPlaceholder,
    disabled,
    inputId,
    isRequired,
    attrs,
    onSearch,
    onSelect,
    wrapperClass,
  }
}
